import { LitElement, html, svg, css, nothing } from 'lit';
import { PRINT_STYLES } from './print-styles.js';
import { multiplesColumns } from '../lib/multiples.js';
import { withFormats } from '../lib/table.js';
import { repeat } from 'lit/directives/repeat.js';
import { StoreMixin } from '../store.js';
import { setFilters } from '../router.js';
import { STATUS_STATES } from '../lib/palette.js';
import {
  FOCUS_CEILING,
  MULTIPLE_SORTS,
  PANEL,
  SCALE_MODES,
  multiplesBackground,
  multiplesExportRows,
  multiplesGeometry,
  multiplesModel,
  multiplesSummary,
  weekX,
} from '../lib/multiples.js';
import { thresholdScale } from '../lib/heatmap.js';
import { hours as formatHours, percent, weekLabel, longDate, count } from '../lib/format.js';

/** What a row of each dimension is called, singular and plural. Matches the heatmap's corner cell. */
const ROW_NOUNS = {
  company: ['Company', 'company'],
  department: ['Department', 'departments'],
  service: ['Service', 'services'],
  office: ['Office', 'offices'],
  individual: ['Person', 'people'],
};

const SORT_LABELS = { peak: 'Peak', average: 'Average', label: 'Name' };
const SORTS = MULTIPLE_SORTS.map((key) => ({ key, label: SORT_LABELS[key] }));

const SCALE_LABELS = { fit: 'Fit the worst peak', focus: 'Focus near capacity' };
const SCALES = SCALE_MODES.map((key) => ({ key, label: SCALE_LABELS[key] }));

/**
 * The small multiples (V-5, PRD 10.3; Phase 25): the heatmap drawn as a grid of sparklines, one
 * per row, every panel on one shared y-scale with the same threshold bands behind it, sorted by
 * peak allocation.
 *
 * The matrix tells the reader which week hurts and the bullet rows tell them by how many hours.
 * This card tells them the *shape*: a ramp toward overload and a one-week spike are the same two
 * red cells in a matrix and two completely different lines here. The grid is only readable
 * because every panel is identical apart from its line — one scale, one background, one size —
 * which is why the scale lives in the model and never in a panel.
 *
 * Like the bullet rows, it draws no data of its own: the page hands it the same
 * `GET /forecast/heatmap` response the matrix is drawn from, so a point and the cell it stands
 * for are the same figure of the same run. Every number comes out of `lib/multiples.js`; the
 * component formats and positions and computes nothing else.
 *
 * Encoding (PRD 7.4): the status vocabulary, as the matrix and the bullet rows. The background
 * is the run's four threshold bands in their tints, the same in every panel; the line is ink,
 * not a status colour, because it crosses bands; a week past the overloaded threshold carries
 * the overloaded dot and the band's icon is in the tooltip and the table. A week with no
 * capacity has no ratio, so the line breaks and the floor is marked.
 *
 * Panels are keyed, so changing the sort or the scale moves them rather than repainting them.
 */
class SmallMultiples extends StoreMixin(LitElement) {
  static properties = {
    data: { attribute: false }, // a GET /forecast/heatmap response, or null
    loading: { type: Boolean },
    error: { type: String },
    week: { type: String }, // the week the bullet rows draw; marked here, set by clicking a point
    _sort: { state: true },
    _scale: { state: true },
    _tableOpen: { state: true },
  };

  constructor() {
    super();
    this.data = null;
    this.loading = false;
    this.error = null;
    this.week = null;
    this._sort = 'peak';
    this._scale = 'fit';
    this._tableOpen = false;
  }

  static styles = [
    PRINT_STYLES,
    css`
      :host {
        display: block;
      }
      .card {
        background: var(--surface);
        border: 1px solid var(--border-soft);
        border-radius: var(--radius-card);
        box-shadow: var(--shadow-card);
        padding: 20px 24px 18px;
      }
      .head {
        display: flex;
        flex-wrap: wrap;
        align-items: baseline;
        gap: 6px 18px;
        margin-bottom: 12px;
      }
      .eyebrow {
        font-family: var(--font-eyebrow);
        font-size: 10px;
        font-weight: 700;
        letter-spacing: 0.16em;
        text-transform: uppercase;
        color: var(--ink-muted);
      }
      h2 {
        margin: 2px 0 0;
        font-size: 20px;
        line-height: 1.2;
        color: var(--ink-strong);
      }
      .sub {
        color: var(--ink-muted);
        font-size: 12.5px;
        max-width: 72ch;
      }
      .kpis {
        margin-left: auto;
        display: flex;
        gap: 18px;
        flex-wrap: wrap;
      }
      .kpi {
        text-align: right;
      }
      .kpi .n {
        font-size: 18px;
        font-weight: 700;
        color: var(--ink-strong);
        font-variant-numeric: tabular-nums;
        line-height: 1.1;
      }
      .kpi .l {
        font-size: 11px;
        color: var(--ink-muted);
      }
      .controls {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 8px 16px;
        margin-bottom: 14px;
        font-size: 12.5px;
        color: var(--ink-muted);
      }
      button.link {
        font: inherit;
        font-size: 12.5px;
        border: 0;
        background: none;
        color: var(--link);
        padding: 0;
        font-weight: 600;
        cursor: pointer;
      }
      .segmented {
        display: inline-flex;
        align-items: center;
        gap: 6px;
      }
      .segmented .group {
        display: inline-flex;
        border: 1px solid var(--control-border);
        border-radius: var(--radius-pill);
        background: var(--control-bg);
        padding: 2px;
      }
      .segmented .group button {
        font: inherit;
        border: 0;
        background: none;
        border-radius: var(--radius-pill);
        padding: 3px 10px;
        font-size: 12.5px;
        font-weight: 600;
        color: var(--ink-muted);
        cursor: pointer;
      }
      .segmented .group button[aria-pressed='true'] {
        color: var(--ink-on-brand);
        background: var(--ink-strong);
      }

      /* ---- the grid ---- */
      .grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(210px, 1fr));
        gap: 14px 18px;
      }
      .panel {
        display: grid;
        gap: 3px;
        min-width: 0;
      }
      .panel .name {
        display: flex;
        align-items: baseline;
        gap: 6px;
        min-width: 0;
        font-size: 12.5px;
        color: var(--ink-strong);
      }
      .panel .name button,
      .panel .name .fixed {
        font: inherit;
        font-weight: 600;
        text-align: left;
        color: inherit;
        background: none;
        border: 0;
        padding: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      .panel .name button {
        cursor: pointer;
      }
      .panel .name button:hover {
        color: var(--link);
        text-decoration: underline;
      }
      .panel .name .rank {
        font-family: var(--font-eyebrow);
        font-size: 10px;
        color: var(--ink-faint);
        font-variant-numeric: tabular-nums;
        flex: none;
      }
      .panel .figures {
        display: flex;
        align-items: baseline;
        gap: 8px;
        font-size: 11.5px;
        font-variant-numeric: tabular-nums;
        color: var(--ink-muted);
        white-space: nowrap;
      }
      .panel .figures .pk {
        font-size: 12.5px;
        font-weight: 700;
        color: var(--ink-strong);
      }
      .plot {
        display: block;
        width: 100%;
        height: 54px;
        background: var(--plot-bg);
        border: 1px solid var(--border-soft);
        border-radius: 3px;
        overflow: hidden;
      }
      .plot:focus-visible {
        outline: 2px solid var(--focus);
        outline-offset: 1px;
      }
      .panel[data-selected='true'] .plot {
        outline: 2px solid var(--focus);
        outline-offset: 1px;
      }
      .panel[data-selected='true'] .name button {
        color: var(--link);
        text-decoration: underline;
      }
      .panel.reference {
        grid-column: 1 / -1;
        max-width: 420px;
        padding-top: 10px;
        border-top: 1px solid var(--border);
      }
      /* The bands: the same four tints behind every panel, so only the line differs. */
      .band[data-band='underload'] {
        fill: var(--status-underload-tint);
      }
      .band[data-band='healthy'] {
        fill: var(--status-healthy-tint);
      }
      .band[data-band='caution'] {
        fill: var(--status-caution-tint);
      }
      .band[data-band='overloaded'] {
        fill: var(--status-overloaded-tint);
      }
      .rule {
        stroke: var(--axis);
        stroke-width: 1;
        vector-effect: non-scaling-stroke;
      }
      .rule[data-rule='capacity'] {
        stroke: var(--ink-muted);
      }
      .rule[data-rule='overloaded'] {
        stroke: var(--status-overloaded);
        stroke-dasharray: 3 3;
      }
      .weekmark {
        stroke: var(--ink-strong);
        stroke-width: 1;
        stroke-dasharray: 2 2;
        vector-effect: non-scaling-stroke;
      }
      .line {
        fill: none;
        stroke: var(--ink-strong);
        stroke-width: 1.75;
        stroke-linejoin: round;
        stroke-linecap: round;
        vector-effect: non-scaling-stroke;
      }
      .dot {
        fill: var(--status-overloaded);
        stroke: var(--surface);
        stroke-width: 1;
        vector-effect: non-scaling-stroke;
      }
      .lone {
        fill: var(--ink-strong);
      }
      /* A week with no capacity: no ratio, so no line. Marked on the floor instead. */
      .gap {
        fill: none;
        stroke: var(--ink-faint);
        stroke-width: 1;
        stroke-dasharray: 2 2;
        vector-effect: non-scaling-stroke;
      }
      .clip {
        fill: var(--ink-strong);
        font-size: 7px;
        font-weight: 700;
      }
      .hit {
        fill: transparent;
        cursor: pointer;
      }
      .empty {
        font-size: 7px;
        fill: var(--ink-muted);
      }

      /* ---- the shared axes, stated once for the grid ---- */
      .axes {
        display: flex;
        flex-wrap: wrap;
        justify-content: space-between;
        gap: 4px 18px;
        margin-bottom: 8px;
        font-size: 11px;
        color: var(--tick-ink);
        font-variant-numeric: tabular-nums;
      }

      /* ---- chrome ---- */
      .legend {
        display: flex;
        flex-wrap: wrap;
        gap: 6px 16px;
        margin-top: 14px;
        padding: 0;
        list-style: none;
        font-size: 12px;
        color: var(--ink-muted);
      }
      .legend li {
        display: inline-flex;
        align-items: center;
        gap: 6px;
      }
      .legend .sw {
        width: 26px;
        height: 12px;
        border-radius: 2px;
        border: 1px solid var(--border-soft);
        flex: none;
      }
      .legend .sw.line {
        height: 0;
        border: 0;
        border-top: 2px solid var(--ink-strong);
        border-radius: 0;
      }
      .legend .sw.rule {
        height: 0;
        border: 0;
        border-top: 1px solid var(--ink-muted);
        border-radius: 0;
      }
      .legend .sw.over {
        width: 10px;
        height: 10px;
        border-radius: 50%;
        background: var(--status-overloaded);
        border: 0;
      }
      .legend .range {
        color: var(--ink-faint);
      }
      .note {
        margin: 10px 0 0;
        font-size: 12px;
        color: var(--ink-muted);
        max-width: 88ch;
      }
      .state {
        padding: 26px 0;
        color: var(--ink-muted);
        font-size: 13px;
      }
      .state.error {
        color: var(--status-overloaded);
      }
      details.table {
        margin-top: 14px;
      }
      details.table summary {
        cursor: pointer;
        font-size: 12.5px;
        color: var(--ink-muted);
      }
      details.table data-table {
        margin-top: 10px;
      }
      @media (max-width: 720px) {
        .card {
          padding: 16px 14px 14px;
        }
        .grid {
          grid-template-columns: 1fr;
        }
        .panel.reference {
          max-width: none;
        }
      }
    `,
  ];

  // -------------------------------------------------------------------------------------------
  // Derived

  get _model() {
    return this.data ? multiplesModel(this.data, { sort: this._sort, scale: this._scale }) : null;
  }

  get _nouns() {
    return ROW_NOUNS[this.data?.dimension] ?? ROW_NOUNS.company;
  }

  _summary(model) {
    return multiplesSummary(model);
  }

  // -------------------------------------------------------------------------------------------
  // Interaction

  /** Clicking a week sets the week the bullet rows draw, as the matrix's column heads do. */
  _selectWeek(weekStart) {
    this.dispatchEvent(
      new CustomEvent('week-change', {
        detail: { week_start: weekStart === this.week ? null : weekStart },
        bubbles: true,
        composed: true,
      }),
    );
  }

  _toggleEntity(series) {
    const { filters } = this.appState;
    const dimension = this.data?.dimension;
    if (!dimension || dimension === 'company' || series.reference) return;
    const already = filters.dimension === dimension && filters.entity === series.key;
    setFilters({ dimension, entity: already ? null : series.key });
  }

  _isSelected(series) {
    const { filters } = this.appState;
    return (
      !series.reference &&
      filters.dimension === this.data?.dimension &&
      filters.entity === series.key
    );
  }

  _showTooltip(event, series, point) {
    const tooltip = this.renderRoot.querySelector('chart-tooltip');
    if (!tooltip) return;
    const state = STATUS_STATES.find((s) => s.key === point.band);
    const exact = (v) => formatHours(v, { exact: true });
    const rows = [
      { label: 'Allocated', value: exact(point.allocated_hours) },
      { label: '  High confidence', value: exact(point.high_confidence_hours) },
      { label: 'Capacity', value: exact(point.capacity_hours) },
      point.headroom_hours !== null && point.headroom_hours < 0
        ? { label: 'Over capacity', value: exact(-point.headroom_hours) }
        : { label: 'Headroom', value: exact(point.headroom_hours) },
    ];
    const facts = [
      point.utilization === null ? 'No capacity' : `${percent(point.utilization)} allocated`,
      state ? `${state.icon} ${state.label}` : null,
      series.peak_week === point.week_start ? 'this row’s peak week' : null,
      point.clipped ? 'past the top of the scale' : null,
    ];
    tooltip.show({
      x: event.clientX ?? 0,
      y: event.clientY ?? 0,
      title: `${series.label} · week of ${longDate(point.week_start)}`,
      rows,
      footer: facts.filter(Boolean).join(' · '),
    });
  }

  _hideTooltip() {
    this.renderRoot.querySelector('chart-tooltip')?.hide();
  }

  // -------------------------------------------------------------------------------------------
  // Marks

  /** What a panel states in words, for a screen reader: the shape read out as its figures. */
  _panelLabel(series) {
    if (!series.has_capacity)
      return `${series.label}: no capacity in any week, ${formatHours(
        series.allocated_hours,
      )} allocated over ${count(series.weeks)} weeks`;
    const over =
      series.weeks_over > 0
        ? `, overloaded in ${count(series.weeks_over)} of ${count(series.weeks)} weeks`
        : ', never overloaded';
    return `${series.label}: peak ${percent(series.peak_utilization)} in the week of ${weekLabel(
      series.peak_week,
    )}, average ${percent(series.average)}${over}`;
  }

  _plot(model, series) {
    const g = multiplesGeometry(series, model.scale);
    const background = multiplesBackground(model.scale, model.thresholds);
    const selected = this.week ? model.weeks.findIndex((w) => w.week_start === this.week) : -1;
    return html`
      <svg
        class="plot"
        viewBox=${`0 0 ${PANEL.width} ${PANEL.height}`}
        preserveAspectRatio="none"
        role="img"
        tabindex="0"
        aria-label=${this._panelLabel(series)}
        @pointerleave=${() => this._hideTooltip()}
        @blur=${() => this._hideTooltip()}
      >
        ${background.bands.map(
          (b) => svg`<rect
            class="band"
            data-band=${b.key}
            x="0"
            y=${b.y}
            width=${PANEL.width}
            height=${b.height}
          ></rect>`,
        )}
        ${background.rules.map(
          (r) => svg`<line
            class="rule"
            data-rule=${r.key}
            x1="0"
            x2=${PANEL.width}
            y1=${r.y}
            y2=${r.y}
          ></line>`,
        )}
        ${
          selected >= 0
            ? svg`<line
                class="weekmark"
                x1=${weekX(selected, model.weeks.length)}
                x2=${weekX(selected, model.weeks.length)}
                y1="0"
                y2=${PANEL.height}
              ></line>`
            : nothing
        }
        ${g.segments.map((d) => svg`<path class="line" d=${d}></path>`)}
        ${
          g.lone
            ? g.points
                .filter((p) => p.y !== null)
                .map((p) => svg`<circle class="lone" cx=${p.x} cy=${p.y} r="1.6"></circle>`)
            : nothing
        }
        ${g.gaps.map(
          (p) => svg`<line
            class="gap"
            x1=${p.x}
            x2=${p.x}
            y1=${PANEL.height - 3}
            y2=${PANEL.height}
          ></line>`,
        )}
        ${g.dots.map((p) => svg`<circle class="dot" cx=${p.x} cy=${p.y} r="2.1"></circle>`)}
        ${g.clipped ? svg`<text class="clip" x="1.5" y="6">↑</text>` : nothing}
        ${
          series.has_capacity
            ? nothing
            : svg`<text class="empty" x=${PANEL.width / 2} y=${PANEL.height / 2 + 2}
                text-anchor="middle"
              >
                no capacity
              </text>`
        }
        ${g.points.map(
          (p) => svg`<rect
            class="hit"
            data-week=${p.week_start}
            x=${Math.max(0, p.x - PANEL.width / (2 * Math.max(1, g.points.length - 1)))}
            y="0"
            width=${PANEL.width / Math.max(1, g.points.length - 1)}
            height=${PANEL.height}
            @pointermove=${(e) => this._showTooltip(e, series, p)}
            @click=${() => this._selectWeek(p.week_start)}
          ></rect>`,
        )}
      </svg>
    `;
  }

  _panel(model, series, rank) {
    const dimension = this.data.dimension;
    return html`
      <div
        class=${`panel ${series.reference ? 'reference' : ''}`}
        data-key=${series.key}
        data-selected=${this._isSelected(series) ? 'true' : 'false'}
      >
        <div class="name">
          ${rank === null ? nothing : html`<span class="rank">${rank}</span>`}
          ${
            dimension === 'company' || series.reference
              ? html`<span class="fixed" title=${series.label}>${series.label}</span>`
              : html`<button
                  type="button"
                  title=${
                    this._isSelected(series)
                      ? 'Clear the entity filter'
                      : `Filter the views to ${series.label}`
                  }
                  @click=${() => this._toggleEntity(series)}
                >
                  ${series.label}
                </button>`
          }
        </div>
        ${this._plot(model, series)}
        <div class="figures">
          <span class="pk"
            >${series.peak_utilization === null ? '—' : percent(series.peak_utilization)}</span
          >
          <span>peak${series.peak_week ? html` · ${weekLabel(series.peak_week)}` : nothing}</span>
          <span>avg ${series.average === null ? '—' : percent(series.average)}</span>
        </div>
      </div>
    `;
  }

  _grid(model) {
    return html`
      ${this._axes(model)}
      <div class="grid">
        ${repeat(
          model.series,
          (s) => s.key,
          (s, i) => this._panel(model, s, this._sort === 'label' ? null : i + 1),
        )}
      </div>
      ${
        model.reference
          ? html`<div class="grid">${this._panel(model, model.reference, null)}</div>`
          : nothing
      }
    `;
  }

  /** The scale and the window, stated once: every panel wears them, so no panel repeats them. */
  _axes(model) {
    const first = model.weeks[0];
    const last = model.weeks.at(-1);
    return html`
      <div class="axes">
        <div class="y">
          <span>Allocation, one scale for every panel:</span>
          <span>${model.scale.values.map((v) => `${Math.round(v * 100)}%`).join(' · ')}</span>
        </div>
        <div class="x">
          ${count(model.weeks.length)} weeks · ${weekLabel(first.week_start)} to
          ${weekLabel(last.week_start)}
        </div>
      </div>
    `;
  }

  _controls(model) {
    return html`
      <div class="controls">
        <div class="segmented" role="group" aria-label="Panel order">
          <span>Sort</span>
          <span class="group">
            ${SORTS.map(
              (s) =>
                html`<button
                  type="button"
                  aria-pressed=${this._sort === s.key ? 'true' : 'false'}
                  @click=${() => (this._sort = s.key)}
                >
                  ${s.label}
                </button>`,
            )}
          </span>
        </div>
        <div class="segmented" role="group" aria-label="Shared scale">
          <span>Scale</span>
          <span class="group">
            ${SCALES.map(
              (s) =>
                html`<button
                  type="button"
                  aria-pressed=${this._scale === s.key ? 'true' : 'false'}
                  @click=${() => (this._scale = s.key)}
                >
                  ${s.label}
                </button>`,
            )}
          </span>
        </div>
        <span>to ${Math.round(model.scale.max * 100)}%</span>
        ${
          this.week
            ? html`<button class="link" type="button" @click=${() => this._selectWeek(this.week)}>
                Clear the marked week (${weekLabel(this.week)})
              </button>`
            : nothing
        }
      </div>
    `;
  }

  _legend(model) {
    const scale = thresholdScale(this.data?.thresholds);
    return html`
      <ul class="legend" aria-label="What the marks mean">
        <li>
          <span class="sw line" aria-hidden="true"></span><span>Allocation, week by week</span>
        </li>
        <li>
          <span class="sw rule" aria-hidden="true"></span>
          <span
            >Rules: capacity (100%) and overloaded
            (${percent(model.thresholds.overloaded_above)})</span
          >
        </li>
        <li>
          <span class="sw over" aria-hidden="true"></span>
          <span>A week past the overloaded threshold</span>
        </li>
        ${scale.map(
          (s) => html`
            <li>
              <span class="sw" style=${`background: var(${s.tint});`} aria-hidden="true"></span>
              <span>${s.label} <span class="range">(${s.range} of capacity)</span></span>
            </li>
          `,
        )}
      </ul>
    `;
  }

  _subtitle(model) {
    if (!model) return '';
    const rowsOf =
      this.data.dimension === 'company'
        ? 'the company'
        : `${count(model.series.length)} ${this._nouns[1]}`;
    return `${rowsOf} over ${count(model.weeks.length)} weeks · one shared scale · sorted by ${SORT_LABELS[
      model.sort
    ].toLowerCase()} · pipeline ${this.data.pipeline_mode}`;
  }

  _kpis(model) {
    const s = this._summary(model);
    return html`
      <div class="kpis">
        <div class="kpi">
          <div class="n">${count(s.rows_over)}</div>
          <div class="l">of ${count(s.rows)} ${this._nouns[1]} overloaded in a week</div>
        </div>
        <div class="kpi">
          <div class="n">${s.worst ? percent(s.worst.peak.utilization) : '—'}</div>
          <div class="l">
            ${s.worst ? `${s.worst.series.label}, ${weekLabel(s.worst.peak.week_start)}` : 'no peak'}
          </div>
        </div>
      </div>
    `;
  }

  _notes(model) {
    const { filters } = this.appState;
    const s = this._summary(model);
    const notes = [];
    notes.push(
      html`<p class="note" data-note="scale">
        Every panel is drawn on one scale (0 to ${Math.round(model.scale.max * 100)}%) with the same
        threshold bands behind it, so a shape means the same thing wherever it appears — that is
        what the grid is for. The scale is measured on the ${this._nouns[1]} alone; the company
        panel beneath is their sum, and on a bad week it runs past the top.
        ${
          model.scale.mode === 'fit'
            ? html`“Focus near capacity” holds the scale near 100% instead, and marks the panels
              that overrun it with ↑.`
            : html`“Fit the worst peak” opens the scale to the worst row instead of clipping at
              ${Math.round(model.scale.max * 100)}%; panels clipped here are marked with ↑.`
        }
      </p>`,
    );
    // What one shared scale costs when one row is far above the rest: said plainly, with the
    // control that fixes it, rather than left for the reader to wonder about. Only worth saying
    // where the scale has been pulled well past capacity — a grid that fits inside it costs
    // nobody anything.
    if (model.scale.mode === 'fit' && model.scale.max > FOCUS_CEILING && model.scale.flattened > 0)
      notes.push(
        html`<p class="note" data-note="flattened">
          The worst row reaches ${percent(s.worst?.peak?.utilization)}, so one scale for the grid
          runs to ${Math.round(model.scale.max * 100)}% and ${count(model.scale.flattened)} of
          ${count(model.scale.staffed)} staffed panels have their whole line inside the bottom
          quarter of it, where a ramp and a spike look alike. Switch to “Focus near capacity” to
          read them, with the tall ones clipped and marked.
        </p>`,
      );
    if (s.no_capacity > 0)
      notes.push(
        html`<p class="note" data-note="no-capacity">
          ${count(s.no_capacity)} ${s.no_capacity === 1 ? 'panel has' : 'panels have'} no capacity
          in any week: role-only demand with nobody staffed to it. There is no ratio to plot, so
          those panels are empty and sort last; the matrix and the bullet rows state their hours.
        </p>`,
      );
    if (this.data.withheld_count > 0)
      notes.push(
        html`<p class="note" data-note="withheld">
          ${count(this.data.withheld_count)}
          ${this.data.withheld_count === 1 ? 'person is' : 'people are'} not shown: your access
          covers yourself and your reports at the individual grain (the company panel still counts
          them).
        </p>`,
      );
    if (filters.entity && filters.dimension === this.data.dimension) {
      const series = model.series.find((r) => r.key === filters.entity);
      notes.push(
        html`<p class="note" data-note="entity">
          ${
            series
              ? `${series.label} is highlighted;`
              : 'The entity in the filter bar is not a panel here;'
          }
          every panel stays in the grid so the shapes can be compared. Click the name again to clear
          the filter.
        </p>`,
      );
    }
    if (filters.grain === 'day')
      notes.push(
        html`<p class="note" data-note="grain">
          The grain is daily, so the window is the two weeks the matrix draws as weekdays: two
          points per panel is a comparison, not a shape. Switch the grain to Week for the whole
          window.
        </p>`,
      );
    notes.push(
      html`<p class="note" data-note="week">
        Click a week in any panel to draw the bullet rows beneath for it; the marked week is the
        dashed rule in every panel.
      </p>`,
    );
    return notes;
  }

  // -------------------------------------------------------------------------------------------
  // Table

  get _columns() {
    // The specification is shared with the server's CSV (lib/multiples.js).
    return withFormats(multiplesColumns({ dimension: this._data?.dimension }), {
      byUnit: {
        hours: (v) => (v === null || v === undefined ? '—' : formatHours(v, { exact: true })),
        percent: (v) => percent(v),
      },
      byKey: {
        week_start: (v) => weekLabel(v),
        peak_week: (v) => weekLabel(v),
        band: (v) => (v ? html`<status-badge state=${v} compact></status-badge>` : '—'),
        over: (v) => (v ? '●' : ''),
      },
    });
  }

  // -------------------------------------------------------------------------------------------
  // Render

  render() {
    const { run } = this.appState;
    const model = this._model;

    let body;
    if (!run) body = html`<div class="state">No forecast run yet — nothing to draw.</div>`;
    else if (this.error) body = html`<div class="state error">${this.error}</div>`;
    else if (!this.data && this.loading) body = html`<div class="state">Loading the window…</div>`;
    else if (!model) body = html`<div class="state">No weeks in this window.</div>`;
    else
      body = html`
        ${this._controls(model)} ${this._grid(model)} ${this._legend(model)} ${this._notes(model)}
        <details
          class="table"
          ?open=${this._tableOpen}
          @toggle=${(e) => (this._tableOpen = e.target.open)}
        >
          <summary>Table view</summary>
          <data-table
            caption=${`Allocation per ${this._nouns[0].toLowerCase()} and week — ${this._subtitle(model)}`}
            export-name="small-multiples"
            page-size="25"
            dense
            .columns=${this._columns}
            .rows=${multiplesExportRows(model)}
          ></data-table>
        </details>
      `;

    return html`
      <div class="card" aria-busy=${this.loading ? 'true' : 'false'}>
        <div class="head">
          <div>
            <div class="eyebrow">Small multiples</div>
            <h2>What shape is each row’s window — a ramp, or a spike?</h2>
            <div class="sub">${this._subtitle(model)}</div>
          </div>
          ${model ? this._kpis(model) : nothing}
        </div>
        ${body}
        <chart-tooltip></chart-tooltip>
      </div>
    `;
  }
}

customElements.define('small-multiples', SmallMultiples);
